export type WifiSecurity = "WPA" | "WEP" | "nopass";

export const SECURITY_OPTIONS = [
  { value: "WPA", label: "WPA/WPA2/WPA3" },
  { value: "WEP", label: "WEP" },
  { value: "nopass", label: "None" },
];

export interface WifiFields {
  ssid: string;
  password: string;
  security: WifiSecurity;
  hidden: boolean;
}

export function pickSecurity(value: unknown): WifiSecurity {
  return value === "WEP" || value === "nopass" ? value : "WPA";
}

// `WIFI:T:…;S:…;P:…;H:true;;` in the order the format is published in, and the closing pair is the last field's
// semicolon and the payload's own. A network with no key is a network the phone asks for the key for, which is a
// thing somebody may well want, so only the name is held out for.
export function writeWifi(fields: WifiFields): string {
  const ssid = fields.ssid.trim();
  if (!ssid) return "";
  const parts = [`T:${fields.security}`, `S:${wifiValue(ssid)}`];
  if (fields.security !== "nopass" && fields.password) parts.push(`P:${wifiValue(fields.password)}`);
  // Omitted rather than written false, a network nobody said was hidden being one nobody said anything about.
  if (fields.hidden) parts.push("H:true");
  return `WIFI:${parts.join(";")};;`;
}

// 32 octets is what 802.11 gives an SSID, and it is octets rather than characters: an emoji in a network name is
// four of them. Longer is a name no access point could have been given, so it is a typo and never a network.
export function ssidProblem(ssid: string): string | null {
  const trimmed = ssid.trim();
  if (!trimmed) return null;
  return new TextEncoder().encode(trimmed).length > MAX_SSID_BYTES ? `A network name is ${MAX_SSID_BYTES} bytes` : null;
}

// The lengths the two standards actually accept, checked because a key one character short joins nothing and says
// nothing about why. Both also take the raw key as hex, which is the second length each of them allows.
export function keyProblem(password: string, security: WifiSecurity): string | null {
  if (security === "nopass" || !password) return null;
  // A hex key is read as the raw bytes rather than as the characters, so each standard accepts a second length; a
  // key that is all hex digits is still a passphrase at the first of them, and is held to whichever it matches.
  const hex = HEX.test(password);
  const length = password.length;
  if (security === "WEP") {
    const ok = length === 5 || length === 13 || hex && (length === 10 || length === 26);
    return ok ? null : "A WEP key is 5 or 13 characters, or 10 or 26 hex digits";
  }
  return length >= 8 && length <= 63 || hex && length === 64
    ? null
    : "A WPA key is 8 to 63 characters, or 64 hex digits";
}

const MAX_SSID_BYTES = 32;

const HEX = /^[0-9a-f]+$/i;

// The five characters the format gives a meaning to. The quotes an all-hex value is sometimes written with are not
// among them: a reader that does not take them back off joins a network whose name has quotation marks in it, which
// is a failure nobody can see the cause of, where an SSID is a string to every reader there has ever been.
function wifiValue(value: string): string {
  return value.replace(/([\\;,:"])/g, "\\$1");
}
